import React, { useEffect, useState, useCallback } from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";
import { collection, onSnapshot, query } from "firebase/firestore";
import { db } from "./firebase";
import { useNavigate } from "react-router-dom";

const mapContainerStyle = { width: "100%", height: "90vh" };
const GOOGLE_MAPS_API_KEY = "AIzaSyDQJx-KDJUimSyyVUwOBRrrzTG22-rvz18";

// ✅ Default Jamshedpur center
const JAMSHEDPUR_CENTER = { lat: 22.793726, lng: 86.247313 };

const markerIcons = {
  vehicle: "tracking-image/car_marker.png",
};

// ✅ Validate coordinate values
const isValidCoordinate = (coord) =>
  typeof coord === "number" && !isNaN(coord) && isFinite(coord);

function VehicleRoute() {
  const [vehicles, setVehicles] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [center, setCenter] = useState(JAMSHEDPUR_CENTER);
  const [error, setError] = useState(null);
  const [googleMaps, setGoogleMaps] = useState(null);
  const navigate = useNavigate();

  // ✅ Calculate center dynamically (if vehicles exist)
  const calculateCenter = useCallback((data) => {
    if (!data.length) return JAMSHEDPUR_CENTER;

    const validCoords = data
      .filter(
        (v) => isValidCoordinate(v.latitude) && isValidCoordinate(v.longitude)
      )
      .map((v) => ({ lat: v.latitude, lng: v.longitude }));
return JAMSHEDPUR_CENTER
    if (!validCoords.length) return JAMSHEDPUR_CENTER;

    const avgLat =
      validCoords.reduce((sum, coord) => sum + coord.lat, 0) / validCoords.length;
    const avgLng =
      validCoords.reduce((sum, coord) => sum + coord.lng, 0) / validCoords.length;
    return { lat: avgLat, lng: avgLng };
  }, []);

  // ✅ Fetch real-time vehicle data (only online vehicles)
  useEffect(() => {
    const usersQuery = query(collection(db, "Users"));
    const unsubscribe = onSnapshot(
      usersQuery,
      (snapshot) => {
        const vehicleData = snapshot.docs
          .map((doc) => {
            const data = doc.data();
            return {
              id: doc.id,
              user: data.user,
              latitude: isValidCoordinate(data.latitude)
                ? Number(data.latitude)
                : null,
              longitude: isValidCoordinate(data.longitude)
                ? Number(data.longitude)
                : null,
              is_online: data.is_online || false,
            };
          })
          // ✅ Only include online vehicles with valid coordinates
          .filter(
            (v) => v.is_online && v.latitude !== null && v.longitude !== null
          );

        setVehicles(vehicleData);

        const filteredVehicles = selectedDriver
          ? vehicleData.filter((v) => v.user === selectedDriver)
          : vehicleData;

        setCenter(calculateCenter(filteredVehicles));
        setError(null);
      },
      (err) => {
        setError("Failed to fetch vehicles: " + err.message);
      }
    );

    return () => unsubscribe();
  }, [calculateCenter, selectedDriver]);

  // ✅ Map load handler
  const onMapLoad = useCallback(() => {
    if (window.google && window.google.maps) {
      setGoogleMaps(window.google.maps);
      setIsMapLoaded(true);
    }
  }, []);

  // ✅ Driver filter
  const handleDriverChange = (e) => {
    setSelectedDriver(e.target.value || null);
  };

  // ✅ Navigate to vehicle details
  const handleMarkerClick = (driver) => {
    navigate(`/vehicle/${driver}`);
  };

  // ✅ Get driver names (only online ones)
  const driverNames = [...new Set(vehicles.map((v) => v.user))].sort();

  // ✅ Helper to get icon safely
  const getVehicleIcon = () => {
    if (!googleMaps) return undefined;
    return {
      url: markerIcons.vehicle,
      scaledSize: new googleMaps.Size(100, 40),
    };
  };

  return (
    <div>
      <div style={{ padding: "10px", background: "#f0f0f0" }}>
        <label htmlFor="driverSelect" style={{ marginRight: "10px" }}>
          Select Driver:
        </label>
        <select
          id="driverSelect"
          value={selectedDriver || ""}
          onChange={handleDriverChange}
          style={{ padding: "5px", minWidth: "150px" }}
        >
          <option value="">All Online Drivers</option>
          {driverNames.map((driver) => (
            <option key={driver} value={driver}>
              {driver}
            </option>
          ))}
        </select>
      </div>

      <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={14}
          onLoad={onMapLoad}
          options={{
            zoomControl: true,
            mapTypeControl: true,
            scaleControl: true,
            streetViewControl: true,
            rotateControl: true,
            fullscreenControl: true,
          }}
        >
          {/* ✅ Loading */}
          {!isMapLoaded && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "white",
                padding: "10px",
                borderRadius: "4px",
              }}
            >
              Loading map...
            </div>
          )}

          {/* ✅ Error message */}
          {error && (
            <div
              style={{
                position: "absolute",
                top: 10,
                left: 10,
                background: "red",
                color: "white",
                padding: "10px",
                borderRadius: "4px",
              }}
            >
              {error}
              <button
                onClick={() => setError(null)}
                style={{
                  marginLeft: "10px",
                  background: "white",
                  color: "red",
                  border: "none",
                  padding: "2px 8px",
                }}
              >
                Dismiss
              </button>
            </div>
          )}

          {/* ✅ No online vehicles fallback */}
          {vehicles.length === 0 && (
            <div
              style={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                background: "white",
                padding: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
              }}
            >
              No online vehicles available.
            </div>
          )}

          {/* ✅ Render Markers */}
          {isMapLoaded &&
            googleMaps &&
            vehicles
              .filter((v) => !selectedDriver || v.user === selectedDriver)
              .map((vehicle) => (
                <Marker
                  key={`vehicle-${vehicle.id}`}
                  position={{
                    lat: vehicle.latitude,
                    lng: vehicle.longitude,
                  }}
                  title={`Driver: ${vehicle.user}`}
                  icon={getVehicleIcon()}
                  onClick={() => handleMarkerClick(vehicle.user)}
                />
              ))}
        </GoogleMap>
      </LoadScript>
    </div>
  );
}

export default VehicleRoute;
